/**
 * Instructor Detail Page Route Definition (V2)
 * Route: /instructor/$instructorId
 *
 * SSR: prefetch instructor detail so the component's useQuery(['instructor', id])
 * is a cache hit on first paint. /v2/instructors/{id} is optional-auth → the
 * server-side fetch returns global data; per-user favourite/review state is CSR.
 * The UI lives in $instructorId.lazy.tsx so the fixture/skeleton graph stays
 * out of the entry chunk; only loader/head (needed for SSR) remain here.
 */
import { createFileRoute } from '@tanstack/react-router';
import { InstructorDetailTemplatePending } from '@/components/v2/page-template-pending';
import { cacheFirst } from '@/lib/queries/cache-first';
import { instructorDetailQuery, type InstructorQueryData } from '@/lib/queries/detail';
import { entityHeadMeta } from '@/lib/utils/site-meta';

type InstructorDetailResult<T> = { ok: true; data: T } | { ok: false };

const toInstructorDetailResult = async <T,>(
  promise: Promise<T>
): Promise<InstructorDetailResult<T>> => {
  try {
    const data = await promise;
    return typeof data === 'object' && data !== null && 'ok' in data && data.ok === false
      ? { ok: false }
      : { ok: true, data };
  } catch {
    return { ok: false };
  }
};

export const Route = createFileRoute('/_public/instructors/$instructorId')({
  // Template-first pending: while this route's component chunk downloads, show the
  // instructor page's own shape (centered avatar hero → chips → cards → sticky
  // CTA, no back header) instead of the full-page RoutePending mascot (audit
  // part 2: a detail click must switch immediately, skeletons included).
  pendingComponent: InstructorDetailTemplatePending,
  loader: async ({ context, params, preload }) => {
    // Key + queryFn come from the shared factory (app/lib/queries/detail.ts) so
    // the loader-seeded entry, the detail page's useQuery and the data
    // prefetchers can never drift apart. Behavior is byte-for-byte the inline
    // shape this used to spell out.
    const instructorQuery = instructorDetailQuery(params.instructorId);
    const refreshInstructor = () => context.queryClient.ensureQueryData(instructorQuery);
    // Preload short-circuit: detail-entry cards fire this loader via
    // <Link preload="viewport"> as they scroll past, and a preload run must
    // never touch the network. Serve the cache when present, else an
    // instantly-resolved cold ({ ok: false }) envelope. The result must
    // SETTLE, never park: preloadRoute keeps the loader lane, its abort
    // controller and match resources alive until the loader settles, so a
    // never-resolving promise would leak a permanently-pending lane for
    // every card scrolled past. The ok:false value is never rendered —
    // commits always re-run this loader (defaultPreloadStaleTime is 0);
    // warming the route chunk is all a preload exists for here.
    // getQueryData is optional-chained for the loader-contract tests, which
    // stub the client with ensureQueryData only; a missing reader simply
    // reads as cold cache.
    if (preload) {
      const cached = context.queryClient.getQueryData?.<InstructorQueryData>(
        instructorQuery.queryKey
      );
      return {
        instructorDetailPromise:
          cached === undefined
            ? Promise.resolve<InstructorDetailResult<InstructorQueryData>>({ ok: false })
            : toInstructorDetailResult(Promise.resolve(cached)),
        instructorDetail: undefined,
      };
    }
    // Cache-first gate: the shared gate (app/lib/queries/data-plane, through
    // cacheFirst) owns the freshness decision, so this loader and the
    // prefetchers can never disagree about whether the cache entry is usable.
    // A cached instructor resolves the envelope immediately and a stale entry
    // is healed in the background (single-flight-deduped with the mounted
    // queries); a cold cache keeps the streamed fetch below, with its existing
    // retry/error paths.
    const { cached, stream } = cacheFirst(
      context.queryClient,
      instructorQuery.queryKey,
      refreshInstructor
    );
    const instructorDetailPromise =
      cached !== undefined
        ? toInstructorDetailResult(Promise.resolve(cached))
        : toInstructorDetailResult(stream());
    // Server-only await so head() can read the entity for SSR OG tags (audit
    // UIUX-02): chat-app crawlers never run JS, so a streamed title would
    // never reach the preview. Failures resolve to ok:false so the body keeps
    // its retry UI instead of the error boundary. Client navigations skip the
    // await — the promise streams into the lazy component's <Await>
    // (EntityDetailLayout skeleton) and head() falls back to the root defaults
    // while the entity is absent (perf audit part 2, Theme 2).
    if (typeof document !== 'undefined') {
      return { instructorDetailPromise, instructorDetail: undefined };
    }
    const instructorDetail = await instructorDetailPromise;
    const instructor =
      instructorDetail.ok && instructorDetail.data.ok ? instructorDetail.data.value : undefined;
    return { instructorDetailPromise, instructorDetail: instructor };
  },
  head: ({ loaderData, params }) => {
    const instructor = loaderData?.instructorDetail;
    if (!instructor) return {};
    return {
      ...entityHeadMeta({
        title: `${instructor.name} - Instructor | TIL`,
        description:
          instructor.genres.length > 0
            ? `${instructor.genres.slice(0, 3).join(', ')} classes with ${instructor.name} on TIL.`
            : `See classes and studios by ${instructor.name} on TIL.`,
        image: instructor.imageUrl,
        // slug is optional on InstructorDetailV2, so fall back to the
        // route param rather than emitting "/instructor/undefined".
        path: `/instructors/${instructor.slug ?? params.instructorId}`,
      }),
    };
  },
});
